<script setup>
import DealCard from './DealCard.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['dealClick', 'dealDropped', 'addDeal']);

const formatCurrency = cents => {
  if (!cents) return 'R$ 0';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(cents / 100);
};

const stageColorClass = color => {
  const stageColors = {
    '#64748b': 'bg-n-slate-9',
    '#3b82f6': 'bg-n-blue-9',
    '#f59e0b': 'bg-n-amber-9',
    '#8b5cf6': 'bg-n-brand',
    '#22c55e': 'bg-n-teal-9',
    '#ef4444': 'bg-n-ruby-9',
    '#1f93ff': 'bg-n-blue-9',
  };

  return stageColors[color?.toLowerCase()] || 'bg-n-brand';
};

const onDragOver = event => {
  event.preventDefault();
  event.currentTarget.classList.add('ring-2', 'ring-n-brand/20');
};

const onDragLeave = event => {
  event.currentTarget.classList.remove('ring-2', 'ring-n-brand/20');
};

const onDrop = event => {
  event.preventDefault();
  event.currentTarget.classList.remove('ring-2', 'ring-n-brand/20');
  const dealId = event.dataTransfer.getData('deal-id');
  if (dealId) {
    emit('dealDropped', {
      dealId: parseInt(dealId, 10),
      stageId: props.stage.id,
    });
  }
};
</script>

<template>
  <div
    class="flex h-full max-h-full w-80 flex-shrink-0 flex-col overflow-hidden rounded-lg border border-n-weak bg-transparent"
  >
    <!-- Header -->
    <div class="border-b border-n-weak px-3 py-3">
      <div class="flex items-start justify-between gap-3">
        <div class="flex min-w-0 items-center gap-2">
          <span
            class="size-2.5 flex-shrink-0 rounded-full"
            :class="stageColorClass(stage.color)"
          />
          <span class="truncate text-sm font-semibold text-n-slate-12">
            {{ stage.name }}
          </span>
        </div>
        <span
          class="flex-shrink-0 rounded-md border border-n-weak bg-n-alpha-1 px-2 py-0.5 text-xs font-medium text-n-slate-11"
        >
          {{ stage.deals?.length || 0 }}
        </span>
      </div>
      <span class="mt-1 block text-xs font-medium text-n-slate-11">
        {{ formatCurrency(stage.total_value) }}
      </span>
    </div>

    <!-- Drop zone -->
    <div
      class="flex min-h-[120px] flex-1 flex-col gap-2 overflow-y-auto bg-transparent p-2 transition-all"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <DealCard
        v-for="deal in stage.deals"
        :key="deal.id"
        :deal="deal"
        @click="emit('dealClick', deal)"
      />

      <div
        v-if="!stage.deals?.length"
        class="grid min-h-32 place-content-center rounded-lg border border-dashed border-n-weak bg-transparent px-4 text-center"
      >
        <span
          class="mx-auto grid size-10 place-content-center rounded-lg bg-n-alpha-2 text-n-slate-10"
        >
          <span class="i-lucide-plus size-4" />
        </span>
        <p class="mt-3 text-sm font-medium text-n-slate-11">
          {{ $t('KANBAN.COLUMN.EMPTY_DROP') }}
        </p>
      </div>

      <!-- Add deal button -->
      <button
        class="flex min-h-9 items-center justify-center gap-1 rounded-md border border-dashed border-n-weak bg-transparent px-3 py-2 text-xs font-medium text-n-slate-11 transition-colors hover:border-n-brand/40 hover:bg-n-brand/5 hover:text-n-slate-12"
        @click="emit('addDeal', stage.id)"
      >
        <span class="i-lucide-plus size-3" />
        {{ $t('KANBAN.ADD_DEAL') }}
      </button>
    </div>
  </div>
</template>
